/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { primitiveColorTokensV2 } from '@uber/base-specs/src/generated/base-color-tokens-v2';
import { HIERARCHY, KIND } from './constants';
import type { Hierarchy, Kind } from './types';
import type { ThemeMode } from '../helpers/types';
type V2BackgroundSwatches = {
  subtleLight: string;
  subtleDark: string;
  boldLight: string;
  boldDark: string;
};

/**
 * Base 2.0 primitive swatches backing Banner's root background, keyed by
 * `kind`. Sourced from the Figma spec's `banner-background-subtle` (used for
 * `HIERARCHY.low`) and `banner-background-bold` (used for `HIERARCHY.high`)
 * definitions. Resolved directly from primitives rather than `theme.colors`
 * so the color stays fixed across product brand treatments (e.g. `ai`),
 * which repaint the `accent*` semantics that `kind: info` used to resolve
 * through.
 */
const V2_BACKGROUND_SWATCHES: Record<Kind, V2BackgroundSwatches> = {
  [KIND.info]: {
    subtleLight: primitiveColorTokensV2.blue98,
    subtleDark: primitiveColorTokensV2.blue23,
    boldLight: primitiveColorTokensV2.blue57,
    boldDark: primitiveColorTokensV2.blue44
  },
  [KIND.negative]: {
    subtleLight: primitiveColorTokensV2.red96,
    subtleDark: primitiveColorTokensV2.red23,
    boldLight: primitiveColorTokensV2.red53,
    boldDark: primitiveColorTokensV2.red44
  },
  [KIND.positive]: {
    subtleLight: primitiveColorTokensV2.green98,
    subtleDark: primitiveColorTokensV2.green27,
    boldLight: primitiveColorTokensV2.green53,
    boldDark: primitiveColorTokensV2.green66
  },
  [KIND.warning]: {
    subtleLight: primitiveColorTokensV2.orange96,
    subtleDark: primitiveColorTokensV2.orange27,
    boldLight: primitiveColorTokensV2.orange74,
    boldDark: primitiveColorTokensV2.orange66
  }
};

/**
 * Resolves Banner's root background color from the Base 2.0 color spec.
 * Only call this when Base 2.0 colors are active (`useBase2Features().colors`);
 * v1 themes keep using the existing `background{Accent,Negative,Positive,Warning}
 * {,Light}` theme color tokens instead.
 */
export function getV2BackgroundColor(kind: Kind, hierarchy: Hierarchy, themeMode: ThemeMode | undefined): string {
  const swatches = V2_BACKGROUND_SWATCHES[kind] ?? V2_BACKGROUND_SWATCHES[KIND.info];
  const isDark = themeMode === 'dark';
  if (hierarchy === HIERARCHY.high) {
    return isDark ? swatches.boldDark : swatches.boldLight;
  }
  return isDark ? swatches.subtleDark : swatches.subtleLight;
}